import { useEffect, useState } from "react";
import api from "../../lib/api";
import DashboardShell from "./DashboardShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const cats = ["security", "dj", "foodtruck", "lighting", "sound", "animation", "photography", "cleaning", "decoration"];

const empty = { category: "dj", name: "", description: "", zone: "", services: "", price_min: 100, price_max: 500 };

export default function ProviderDashboard() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/my/provider");
    setList(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      services: typeof form.services === "string"
        ? form.services.split(",").map(s => s.trim()).filter(Boolean)
        : form.services,
      images: form.images || [],
    };
    try {
      if (editingId) {
        await api.put(`/providers/${editingId}`, payload);
        toast.success("Profil mis à jour");
      } else {
        await api.post("/providers", payload);
        toast.success("Profil prestataire créé");
      }
      setForm(empty);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      category: p.category,
      name: p.name,
      description: p.description,
      zone: p.zone,
      services: (p.services || []).join(", "),
      price_min: p.price_min,
      price_max: p.price_max,
      images: p.images || [],
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(empty);
  };

  const remove = async (id) => {
    if (!confirm("Supprimer définitivement ce service ?")) return;
    try {
      await api.delete(`/providers/${id}`);
      toast.success("Supprimé");
      if (editingId === id) cancelEdit();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <DashboardShell title="Profil prestataire" subtitle="Présentez vos services">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <form onSubmit={submit} className="bg-white border border-ink/10 rounded-md p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-medium text-lg text-ink">
              {editingId ? "Modifier le service" : "Ajouter un service"}
            </h2>
            {editingId && (
              <button type="button" onClick={cancelEdit} className="text-xs text-muted-foreground flex items-center gap-1 hover:text-ink" data-testid="provider-cancel-edit">
                <X className="h-3 w-3" /> Annuler
              </button>
            )}
          </div>
          <div>
            <Label>Catégorie</Label>
            <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
              <SelectTrigger data-testid="provider-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {cats.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Nom du service</Label><Input data-testid="provider-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} required /></div>
          <div><Label>Zone d'intervention</Label><Input value={form.zone} onChange={(e) => setForm({...form, zone: e.target.value})} placeholder="Île-de-France, France entière..." required /></div>
          <div><Label>Services (séparés par virgule)</Label><Input value={form.services} onChange={(e) => setForm({...form, services: e.target.value})} placeholder="DJ, sonorisation, light show" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Prix min (€)</Label><Input type="number" value={form.price_min} onChange={(e) => setForm({...form, price_min: parseFloat(e.target.value)})} /></div>
            <div><Label>Prix max (€)</Label><Input type="number" value={form.price_max} onChange={(e) => setForm({...form, price_max: parseFloat(e.target.value)})} /></div>
          </div>
          <Button type="submit" data-testid="provider-submit" className="w-full bg-ink hover:bg-ink/85 text-cream rounded-none">
            {editingId ? "Mettre à jour" : "Publier"}
          </Button>
        </form>

        <div className="space-y-3">
          <h2 className="font-heading font-medium text-lg text-ink">Mes services</h2>
          {list.length === 0 ? <p className="text-muted-foreground">Aucun service publié.</p> :
            list.map((p) => (
              <div key={p.id} className="bg-white border border-ink/10 rounded-md p-4" data-testid={`provider-item-${p.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-accent uppercase tracking-wider mb-1">{p.category}</p>
                    <p className="font-heading font-medium text-ink">{p.name}</p>
                    <p className="text-sm text-muted-foreground line-clamp-2">{p.description}</p>
                    <p className="text-sm font-bold text-ink mt-2">{p.price_min}€ – {p.price_max}€</p>
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <button onClick={() => startEdit(p)} title="Modifier" data-testid={`provider-edit-${p.id}`} className="p-2 hover:bg-cream-100 rounded-md">
                      <Pencil className="h-3.5 w-3.5 text-ink/70" />
                    </button>
                    <button onClick={() => remove(p.id)} title="Supprimer" data-testid={`provider-delete-${p.id}`} className="p-2 hover:bg-destructive/10 rounded-md">
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </DashboardShell>
  );
}
